<template>
  <canvas ref="canvas" class="editor-content"></canvas>
</template>

<script>
import * as PIXI from 'pixi.js'
import { resizeBase64, loadTexture, getItem, setItem, changeImageColor, rgbToHex } from '@/utils/Utils.js'
import MyPIXIText from '@/pixi/Text.js'
import MyPIXIImage from '@/pixi/Image.js'
import moment from 'dayjs'
import loadImages from 'image-promise'
import { v4 as uuidv4 } from 'uuid'
import Hammer from 'hammerjs'

let cropData = null
let app = null
let appContainer = null
let bg = null
let focusContainer = null
let overlay = null
let mask = null
let frame = null
let frameline = null
let gray = null
let transparent = null
let itemlist = []
let points = null

const frameStyle = {
  size: 30,
  border: 30
}

export default {
  data () {
    return {
      temImageData: {
        name: '',
        src: ''
      },
      selected: null
    }
  },
  methods: {
    getRepeat () {
      let ret = false
      if (bg) {
        ret = bg.isRepeat || false
      }
      return ret
    },
    async toggleRepeat () {
      bg.isRepeat = !(bg.isRepeat || false)
      if (bg.isRepeat) {
        bg.width = bg.width * 100
        bg.height = bg.height * 100
        this.$store.state.editor.scale = 0
        this.scale(1 + 0 * 0.1)
        this.$emit('setScale')
      } else {
        bg.width = bg.width / 100
        bg.height = bg.height / 100
        this.$store.state.editor.scale = 0
        this.scale(1 + 0 * 0.1)
        this.$emit('setScale')
      }
      return bg.isRepeat
    },
    // 捏合手势
    onPinch (e) {
      e.preventDefault()
      if (e.type === 'pinchstart') {
        bg.canDrag = false
        bg.initScale = bg.scale.y
      } else if (e.type === 'pinchend') {
        bg.canDrag = true
        bg.scale.y = parseFloat(bg.scale.y.toFixed(2))
        this.$store.state.editor.zoom = Math.trunc(bg.scale.y * 100)
        this.$emit('setZoom')
      } else {
        let scale = bg.initScale * e.scale
        if (scale > 2) {
          scale = 2
        } else if (scale < 0.05) {
          scale = 0.05
        }
        bg.scale.set(bg.flip * scale, scale)
      }
    },
    // 旋转手势
    onRotate (e) {
      e.preventDefault()
      if (e.type === 'rotatestart') {
        bg.canDrag = false
        bg.lastRotation = bg.angle
        bg.startRotation = e.rotation
        bg.lockRotate = true
      } else if (e.type === 'rotateend') {
        bg.canDrag = true
        bg.angle = Math.trunc(bg.angle)
        this.$store.state.editor.rotation = bg.angle - bg._startAngle
        this.$emit('setRotation')
      } else if (e.type === 'rotatemove') {
        const step = bg.startRotation - e.rotation
        if (Math.abs(step) > 20) {
          bg.lockRotate = false
        }
        if (bg.lockRotate) return
        const value = bg.lastRotation - (step - 20)
        bg.angle = value
        if (bg.angle < -45) {
          bg.angle = -45
        } else if (bg.angle > 45) {
          bg.angle = 45
        }
      }
    },
    initScene () {
      if (!this.$refs.canvas) return
      if (!app) {
        app = new PIXI.Application({
          view: this.$refs.canvas,
          width: this.$refs.canvas.parentElement?.clientWidth,
          height: this.$refs.canvas.parentElement?.clientHeight,
          // backgroundColor: 0xffffff,
          transparent: true,
          resolution: !this.$store.state.ui.os.isPC ? 1 : window.devicePixelRatio,
          antialias: false
        })

        var hammer = new Hammer.Manager(app.view)
        var pinch = new Hammer.Pinch()
        var rotate = new Hammer.Rotate()
        pinch.recognizeWith(rotate)
        hammer.add(pinch)
        hammer.add(rotate)
        hammer.on('pinchstart pinchin pinchout pinchend', (e) => {
          this.onPinch(e)
        })
        hammer.on('rotatestart rotatemove rotateend', (e) => {
          this.onRotate(e)
        })

        appContainer = new PIXI.Container()
        appContainer.x = app.view.width / 2
        appContainer.y = app.view.height / 2
        app.stage.addChild(appContainer)
      }

      // bg
      // items
      // gray
      // frame
      // points
      // overlay
      const bgContainer = new PIXI.Container()
      appContainer.addChild(bgContainer)

      const containerItems = new PIXI.Container()
      appContainer.addChild(containerItems)

      const containerGray = new PIXI.Container()
      appContainer.addChild(containerGray)

      const containerFrame = new PIXI.Container()
      appContainer.addChild(containerFrame)

      points = new PIXI.Container()
      appContainer.addChild(points)

      const containerOverlay = new PIXI.Container()
      appContainer.addChild(containerOverlay)
    },
    select (target) {
      itemlist.forEach((item) => {
        item.unhighlight()
      })
      if (target && target.highlight) {
        target.highlight()
      }
      this.$emit('select', target)
      this.selected = target
      this.$store.commit('selectCrop', target?.entity)
      // console.log('选中', target)
    },
    addAppMask () {
      mask = new PIXI.Graphics()
      mask.beginFill(0x000000)
      mask.drawRect(-cropData.size.width * 0.5, -cropData.size.height * 0.5, cropData.size.width, cropData.size.height)
      mask.endFill()
      mask.visible = false
      appContainer.addChild(mask)
    },
    setAppMask () {
      if (mask) {
        appContainer.removeChild(mask)
      }
      mask = new PIXI.Graphics()
      mask.beginFill(0x000000)
      mask.drawRect(overlay.x - overlay.width * 0.5, overlay.y - overlay.height * 0.5, overlay.width, overlay.height)
      mask.endFill()
      mask.visible = false
      appContainer.addChild(mask)
    },
    async addBG (width) {
      transparent = new PIXI.TilingSprite(PIXI.Texture.from('./transparent.png'))
      transparent.anchor.set(0.5)
      transparent._type = 'transparent'
      transparent.width = app.view.width
      transparent.height = app.view.height
      transparent.interactive = true
      transparent.on('pointerdown', () => {
        this.select(transparent)
      })
      appContainer.getChildAt(0).addChild(transparent)

      const texture = await loadTexture(cropData.src)
      bg = new PIXI.TilingSprite(texture)
      bg.flip = 1
      bg.canDrag = true
      bg.uvRespectAnchor = true
      // bg = new PIXI.Sprite(texture)
      bg._startAngle = 0
      bg._type = 'bg'
      bg.anchor.set(0.5)
      bg.interactive = true
      bg._dragging = false
      bg.on('pointerdown', (e) => {
        if (!bg.canDrag) return
        this.select(bg)
        bg._dragging = true
        bg._startx = e.data.getLocalPosition(bg.parent).x
        bg._starty = e.data.getLocalPosition(bg.parent).y
        bg._offsetx = e.data.getLocalPosition(bg.parent).x - bg.x
        bg._offsety = e.data.getLocalPosition(bg.parent).y - bg.y
      })
      bg.on('pointerup', () => {
        if (!bg.canDrag) return
        bg._dragging = false
      })
      bg.on('pointerupoutside', () => {
        if (!bg.canDrag) return
        bg._dragging = false
      })
      bg.on('pointermove', (e) => {
        if (!bg.canDrag) return
        if (!bg._dragging) return
        bg.x = -bg._offsetx + e.data.getLocalPosition(bg.parent).x
        bg.y = -bg._offsety + e.data.getLocalPosition(bg.parent).y
      })

      if (this.$store.state.crop.crops.bg) {
        bg.isRepeat = this.$store.state.crop.crops.bg.isRepeat
        bg._startHeight = this.$store.state.crop.crops.bg._startHeight
        bg.angle = this.$store.state.crop.crops.bg.angle
        bg._startAngle = this.$store.state.crop.crops.bg._startAngle
        bg.x = this.$store.state.crop.crops.bg.x
        bg.y = this.$store.state.crop.crops.bg.y
        bg.scale.set(this.$store.state.crop.crops.bg.flip * (this.$store.state.crop.crops.bg.zoom * 0.01), (this.$store.state.crop.crops.bg.zoom * 0.01))
      } else {
        bg._startWidth = bg.width
        bg._startHeight = bg.height
        bg._startAngle = 0
      }

      // 默认宽度撑满
      const defaultScale = width / texture._orignWidth
      let scale = defaultScale
      bg._tileScale = defaultScale
      if (this.$store.state.crop.crops.bg) {
        scale = bg._tileScale * (1 + this.$store.state.crop.crops.bg.scale * 0.1)
      }
      bg.tilePosition.x = 0.5 * texture._orignWidth * scale
      bg.tilePosition.y = 0.5 * texture._orignHeight * scale
      bg.tileScale.set(scale, scale)

      if (this.$store.state.crop.crops.bg) {
        bg.width = (bg.isRepeat ? this.$store.state.crop.crops.bg.width * 100 : this.$store.state.crop.crops.bg.width)
        bg.height = (bg.isRepeat ? this.$store.state.crop.crops.bg.height * 100 : this.$store.state.crop.crops.bg.height)
      } else {
        bg.width = width
        bg.height = width / texture._orignWidth * texture._orignHeight
      }

      appContainer.getChildAt(0).addChild(bg)
      // select(bg)
    },
    addGray () {
      gray = new PIXI.Sprite(PIXI.Texture.WHITE)
      gray.alpha = 0.8
      gray.anchor.set(0.5)
      gray.width = app.view.width
      gray.height = app.view.height
      appContainer.getChildAt(2).addChild(gray)
    },
    addOverlay (width, height) {
      overlay = new PIXI.Sprite(PIXI.Texture.EMPTY)
      if (!cropData.mask) {
        overlay = new PIXI.Sprite(PIXI.Texture.EMPTY)
      } else {
        if (!cropData.mask.startsWith('http') && !cropData.mask.startsWith('data:image/')) {
          cropData.mask = 'data:image/png;base64,' + cropData.mask
        }
        overlay = new PIXI.Sprite(PIXI.Texture.from(cropData.mask))
      }
      // overlay.alpha = 0.3
      overlay.anchor.set(0.5)
      overlay.width = width
      overlay.height = height
      // console.log('overlay', cropData.size.width, cropData.size.height, width, height)
      appContainer.getChildAt(5).addChild(overlay)
    },
    setGrayMask () {
      if (gray.mask) {
        appContainer.getChildAt(2).removeChild(gray.mask)
      }
      const mask = new PIXI.Graphics()
      mask.beginFill(0x000000)
      mask.drawRect(-app.view.width * 0.5, -app.view.height * 0.5, app.view.width, app.view.height)
      mask.beginHole()
      // 希望锚点 0, 0  当前锚点 overlay.anchor.x
      mask.drawRect(overlay.x - overlay.width * overlay.anchor.x, overlay.y - overlay.height * overlay.anchor.y, overlay.width, overlay.height)
      mask.endHole()
      mask.endFill()
      appContainer.getChildAt(2).addChild(mask)
      gray.mask = mask

      // 编辑框
      appContainer.getChildAt(3).getChildAt(0).removeChildAt(0)
      const line = new PIXI.Graphics()
      frameline = line
      line.lineStyle(1, 0x000000, 1)
      line.drawRect(overlay.x - overlay.width * overlay.anchor.x, overlay.y - overlay.height * overlay.anchor.y, overlay.width, overlay.height)
      appContainer.getChildAt(3).getChildAt(0).addChild(line)
      // 重新计算所有元素的_x, _y
      this.$store.commit('calcCrops', {
        x: overlay.x,
        y: overlay.y,
        width: overlay.width,
        height: overlay.height,
        sizeX: this.$store.state.editor.inprint.sizeX,
        sizeY: this.$store.state.editor.inprint.sizeY,
        appWidth: app.view.width,
        appHeight: app.view.height
      })
    },
    focusWindow () {
      frame.x -= overlay.x
      frame.y -= overlay.y
      bg.x -= overlay.x
      bg.y -= overlay.y
      overlay.x = 0
      overlay.y = 0
      focusContainer.visible = false
      this.setGrayMask()
    },
    addFrame () {
      // 编辑框
      const lineContainer = new PIXI.Container()
      appContainer.getChildAt(3).addChild(lineContainer)
      const line = new PIXI.Graphics()
      frameline = line
      line.lineStyle(1, 0x000000, 1)
      line.drawRect(overlay.x - overlay.width * 0.5, overlay.y - overlay.height * 0.5, overlay.width, overlay.height)
      lineContainer.addChild(line)

      frame = new PIXI.Container()
      frame.x = overlay.x
      frame.y = overlay.y
      appContainer.getChildAt(3).addChild(frame)

      const topLeft = new PIXI.Sprite(PIXI.Texture.from('./circle.svg'))
      topLeft.tint = 0x000000
      const topRight = new PIXI.Sprite(PIXI.Texture.from('./circle.svg'))
      topRight.tint = 0x000000
      const bottomLeft = new PIXI.Sprite(PIXI.Texture.from('./circle.svg'))
      bottomLeft.tint = 0x000000
      const bottomRight = new PIXI.Sprite(PIXI.Texture.from('./circle.svg'))
      bottomRight.tint = 0x000000

      focusContainer = new PIXI.Container()
      focusContainer.visible = false
      const focusBG = new PIXI.Graphics()
      focusBG.beginFill(0xffffff)
      focusBG.drawCircle(0, 0, 25)
      focusBG.endFill()
      focusContainer.addChild(focusBG)

      const focusSprite = new PIXI.Sprite(PIXI.Texture.from('./focus.svg'))
      focusSprite.anchor.set(0.5)
      focusSprite.width = 30
      focusSprite.height = 30
      focusSprite.x = 0
      focusSprite.y = 0
      focusSprite.interactive = true
      focusSprite.on('pointerdown', () => {
        this.focusWindow()
      })
      focusContainer.addChild(focusSprite)

      frame.addChild(topLeft)
      frame.addChild(topRight)
      frame.addChild(bottomLeft)
      frame.addChild(bottomRight)
      frame.addChild(focusContainer)

      // 锚点
      // 0,0        1,0
      //    0.5,0.5
      // 0,1        1,1

      topLeft.anchor.set(0.5)
      topLeft.width = frameStyle.size
      topLeft.height = frameStyle.size
      topLeft.x = -overlay.width * 0.5
      topLeft.y = -overlay.height * 0.5

      topRight.anchor.set(0.5)
      topRight.width = frameStyle.size
      topRight.height = frameStyle.size
      topRight.x = overlay.width * 0.5
      topRight.y = -overlay.height * 0.5

      bottomLeft.anchor.set(0.5)
      bottomLeft.width = frameStyle.size
      bottomLeft.height = frameStyle.size
      bottomLeft.x = -overlay.width * 0.5
      bottomLeft.y = overlay.height * 0.5

      bottomRight.anchor.set(0.5)
      bottomRight.width = frameStyle.size
      bottomRight.height = frameStyle.size
      bottomRight.x = overlay.width * 0.5
      bottomRight.y = overlay.height * 0.5
    },
    async crop () {
      if (!app) return null
      this.select(null)
      this.setAppMask()
      this.addPoints()
      mask.visible = true
      appContainer.mask = mask

      gray.visible = false
      overlay.visible = false
      frame.visible = false
      frameline.visible = false
      transparent.visible = false

      const base64 = app.renderer.plugins.extract.base64(app.stage)
      // console.log(base64)

      appContainer.mask = null
      mask.visible = false

      overlay.visible = true
      frame.visible = true
      frameline.visible = true
      transparent.visible = true
      gray.visible = true

      // 转换尺寸
      const newbase64 = await resizeBase64(base64, cropData.size.width, cropData.size.height)
      return {
        src: bg.texture.baseTexture.resource ? bg.texture.baseTexture.resource.url : '',
        crop: newbase64
      }
    },
    rotateLeft () {
      if (!bg) return
      bg.angle -= 90
      bg._startAngle -= 90
    },
    flip () {
      if (!bg) return
      bg.scale.x *= -1
      bg.flip = -1
    },
    rotate (val) {
      if (!bg) return
      bg.angle = bg._startAngle + val
    },
    scale (val) {
      if (!bg) return
      const newScale = val * bg._tileScale
      const deltaX = (bg.tilePosition.x / bg.tileScale.x) * (newScale - bg.tileScale.x)
      const deltaY = (bg.tilePosition.y / bg.tileScale.y) * (newScale - bg.tileScale.y)
      bg.tilePosition.x += deltaX
      bg.tilePosition.y += deltaY
      bg.tileScale.set(newScale, newScale)
    },
    zoom (val) {
      if (!bg) return
      val = val * 0.01
      bg.scale.set(val, val)
      // bg.width = bg._startWidth * val
      // bg.height = bg._startHeight * val
    },
    removeItem (id) {
      itemlist.forEach((item, index) => {
        if (item.id === id) {
          itemlist.splice(index, 1)
        }
      })
    },
    getTextEntity (text) {
      return {
        type: 'text',
        id: text.id,
        text: text.text.text,
        color: text.text.style.fill,
        fontSize: text.text.style.fontSize,
        fontFamily: text.text.style.fontFamily,
        fontWeight: text.text.style.fontWeight,
        fontStyle: text.text.style.fontStyle,
        angle: text.container.angle,
        width: text.text.width,
        height: text.text.height,
        x: text.container.x,
        y: text.container.y,
        _width: text.text.width / overlay.width * this.$store.state.editor.inprint.sizeX,
        _height: text.text.height / overlay.height * this.$store.state.editor.inprint.sizeY,
        _x: (text.container.x - overlay.x) / overlay.width * this.$store.state.editor.inprint.sizeX,
        _y: -(text.container.y + text.text.height * 0.5 - (overlay.y + overlay.height * 0.5)) / overlay.height * this.$store.state.editor.inprint.sizeY
      }
    },
    resizeItem (offset) {
      if (!this.selected) return
      if (this.selected._type === 'text' || this.selected._type === 'image') {
        this.selected.resize(offset)
      }
    },
    rotateItem (angle) {
      if (!this.selected) return
      if (this.selected._type === 'text' || this.selected._type === 'image') {
        this.selected.rotate(angle)
      }
    },
    moveItem (position) {
      if (!this.selected) return
      if (this.selected._type === 'text') {
        const x = position.x / this.$store.state.editor.inprint.sizeX * overlay.width + overlay.x
        const y = -position.y / this.$store.state.editor.inprint.sizeY * overlay.height - this.selected.text.height * 0.5 + overlay.y + overlay.height * 0.5
        this.selected.move({ x, y })
      } else if (this.selected._type === 'image') {
        const x = position.x / this.$store.state.editor.inprint.sizeX * overlay.width + overlay.x
        const y = -position.y / this.$store.state.editor.inprint.sizeY * overlay.height - this.selected.sprite.height * 0.5 + overlay.y + overlay.height * 0.5
        this.selected.move({ x, y })
      }
    },
    addText (data) {
      const text = new MyPIXIText(app, data, !this.$store.state.ui.os.isPC)
      if (!data) {
        text.id = uuidv4()
      } else {
        text.id = data.id
      }
      text.focus = () => {
        this.$store.state.ui.keyboard = true
      }
      text.blur = () => {
        setTimeout(() => {
          this.$store.state.ui.keyboard = false
        }, 300)
        // 如果文本为空，删除
        if (text.input.value === '') {
          text.removeAction()
        }
      }
      text.select = (val) => {
        this.select(val)
      }
      text.remove = () => {
        this.removeItem(text.id)
        this.$store.commit('removeCropItem', {
          id: text.id
        })
        this.select(null)
      }
      text.update = () => {
        if (Math.abs(text.container.x - overlay.x) < 20) {
          text.container.x = overlay.x
        }
        if (Math.abs(text.container.y - overlay.y) < 20) {
          text.container.y = overlay.y
        }
        text.entity = this.getTextEntity(text)
        this.$store.commit('updateCropItem', {
          item: text.entity
        })
        // console.log(text.container.x, text.container.y, text.container.angle)
      }
      itemlist.push(text)
      appContainer.getChildAt(1).addChild(text.container)
      text.entity = this.getTextEntity(text)
      if (!data) {
        this.selected = text
        this.select(text)
        this.$store.commit('addCropItem', {
          item: text.entity
        })
      }
    },
    async changeBG (file) {
      let image = null
      if (file) {
        image = new Image()
        image.src = file
        await loadImages(image)
        const texture = PIXI.Texture.from(file)
        texture._orignWidth = image.width
        texture._orignHeight = image.height
        bg.texture = texture
        // 宽度撑满
        bg.width = overlay.width
        // 等比例设置高度
        if (image) {
          bg.height = bg.width / image.width * image.height
        } else {
          bg.width = overlay.height
        }
        // 设置tilescale
        // 默认宽度撑满
        const scale = bg.width / texture._orignWidth
        bg._tileScale = scale
        bg.tilePosition.x = 0.5 * texture._orignWidth * scale
        bg.tilePosition.y = 0.5 * texture._orignHeight * scale
        bg.tileScale.set(scale, scale)
        bg.scale.set(1, 1)
        // 重置滑动条
        this.$store.state.editor.scale = 0
        this.$emit('setScale')
      } else {
        bg.texture = PIXI.Texture.EMPTY
        bg.width = 0
        bg.height = 0
      }
      bg.isRepeat = false
      // 重置位置
      bg.x = overlay.x
      bg.y = overlay.y
      // 重置旋转
      bg.angle = 0
      bg._startAngle = 0
      this.$store.state.editor.zoom = 100
      this.$store.state.editor.rotation = 0
      this.$store.state.editor.scale = 0
      this.$emit('setZoom')
      this.$emit('setRotation')
      this.$emit('setScale')
      bg._startWdith = bg.width
      bg._startHeight = bg.height
      if (file) {
        this.select(bg)
      }
    },
    getImageEntity (image) {
      return {
        type: 'image',
        id: image.id,
        name: image.name,
        width: image.sprite.width,
        height: image.sprite.height,
        angle: image.container.angle,
        x: image.container.x,
        y: image.container.y,
        _width: image.sprite.width / overlay.width * this.$store.state.editor.inprint.sizeX,
        _height: image.sprite.height / overlay.height * this.$store.state.editor.inprint.sizeY,
        _x: (image.container.x - overlay.x) / overlay.width * this.$store.state.editor.inprint.sizeX,
        _y: -(image.container.y + image.sprite.height * 0.5 - (overlay.y + overlay.height * 0.5)) / overlay.height * this.$store.state.editor.inprint.sizeY
      }
    },
    async addLogo (file, data, source) {
      // console.log('addlogo', file, data, source)
      const image = new MyPIXIImage()
      if (!data) {
        image.id = uuidv4()
      } else {
        image.id = data.id
      }
      image.entity = {}
      if (data != null) {
        image.name = data.name
        image.entity.source = data.source
        image.entity.color = data.color || '#ffffff'
      } else {
        image.name = moment().format('YYYYMMDDHHmmss') + '.png'
      }
      if (source != null) {
        image.entity.source = source
      }
      image.select = (val) => this.select(val)
      image.remove = () => {
        this.removeItem(image.id)
        this.$store.commit('removeCropItem', {
          id: image.id
        })
        this.select(null)
      }
      image.update = () => {
        if (Math.abs(image.container.x - overlay.x) < 20) {
          image.container.x = overlay.x
        }
        if (Math.abs(image.container.y - overlay.y) < 20) {
          image.container.y = overlay.y
        }
        image.entity = Object.assign(image.entity, this.getImageEntity(image))
        this.$store.commit('updateCropItem', {
          item: image.entity
        })
        // console.log(image.container.x, image.container.y, image.container.angle)
      }
      await image.load(file, data)
      itemlist.push(image)
      appContainer.getChildAt(1).addChild(image.container)
      image.entity = Object.assign(image.entity, this.getImageEntity(image))
      if (!data) {
        this.selected = image
        this.select(image)
        this.$store.commit('addCropItem', {
          item: image.entity
        })
      }
    },
    changeText (e) {
      if (!this.selected) return
      if (!this.selected.text) return
      if (!this.selected.text.style) return
      if (!this.selected.text.style[e.valueProp]) return
      this.selected.text.style[e.valueProp] = e.value
      this.selected.change()
    },
    async recover () {
      if (this.$store.state.crop.crops.items == null) return
      for (let k = 0; k < this.$store.state.crop.crops.items.length; k++) {
        const item = this.$store.state.crop.crops.items[k]
        if (item.type === 'text') {
          this.addText(item)
        } else if (item.type === 'image') {
          await this.addLogo(null, item)
        }
      }
    },
    async load (cropdata) {
      cropData = cropdata
      // bg
      // items
      // gray
      // frame
      // points
      // overlay

      this.initScene()

      // 考虑overlay尺寸的最大值
      let width = app.view.width - frameStyle.border * 2
      let height = width / cropData.size.width * cropData.size.height
      if (height > app.view.height - frameStyle.border * 2) {
        height = app.view.height - frameStyle.border * 2
        width = height / cropData.size.height * cropData.size.width
      }
      await this.addBG(width)
      // appContainer.getChildAt(0).addChild(bg)

      this.addOverlay(width, height)
      // appContainer.getChildAt(5).addChild(overlay)

      this.addGray()
      // appContainer.getChildAt(2).addChild(gray)

      this.addFrame()
      // appContainer.getChildAt(3).addChild(lineContainer)

      this.addAppMask()
      // appContainer.addChild(mask)

      this.setGrayMask()
      // appContainer.getChildAt(2).addChild(mask)

      // 恢复设计
      await this.recover()
      // appContainer.getChildAt(1).addChild(text)
    },
    addPoints () {
      points.removeChildren(0, points.children.length)

      const topleft = new PIXI.Graphics()
      topleft.beginFill(0xffffff)
      topleft.drawCircle(overlay.x - overlay.width * 0.5, overlay.y - overlay.height, 1)
      topleft.endFill()
      points.addChild(topleft)

      const topright = new PIXI.Graphics()
      topright.beginFill(0xffffff)
      topright.drawCircle(overlay.x + overlay.width * 0.5, overlay.y - overlay.height, 1)
      topright.endFill()
      points.addChild(topright)

      const bottomright = new PIXI.Graphics()
      bottomright.beginFill(0xffffff)
      bottomright.drawCircle(overlay.x + overlay.width * 0.5, overlay.y + overlay.height, 1)
      bottomright.endFill()
      points.addChild(bottomright)

      const botomleft = new PIXI.Graphics()
      botomleft.beginFill(0xffffff)
      botomleft.drawCircle(overlay.x - overlay.width * 0.5, overlay.y + overlay.height, 1)
      botomleft.endFill()
      points.addChild(botomleft)
    },
    async saveImageLocal () {
      for (let i = 0; i < itemlist.length; i++) {
        if (itemlist[i]._type === 'image') {
          if (itemlist[i].sprite.texture.baseTexture.resource) {
            const base64 = itemlist[i].sprite.texture.baseTexture.resource.url
            await setItem(itemlist[i].name, base64)
          }
        }
      }
    },
    clear () {
      if (!app) return
      appContainer.removeChildren(0, appContainer.children.length).forEach((c) => {
        c.destroy({
          children: true,
          texture: true,
          baseTexture: true
        })
      })
      cropData = null
      itemlist.forEach(item => {
        if (item._type === 'text') {
          item.destroy()
        }
      })
      itemlist = []
      this.selected = null
      bg = null
      focusContainer = null
    },
    async done () {
      if (!bg) return
      await this.saveImageLocal()
      // 保存bg
      this.$store.commit('updateCrop', {
        valueProp: 'bg',
        value: {
          zoom: this.$store.state.editor.zoom,
          rotation: this.$store.state.editor.rotation,
          scale: this.$store.state.editor.scale,
          flip: bg.scale.x < 0 ? -1 : 1,
          x: bg.x,
          y: bg.y,
          angle: bg.angle,
          _startAngle: bg._startAngle,
          width: bg.isRepeat ? bg.width / 100 : bg.width,
          height: bg.isRepeat ? bg.height / 100 : bg.height,
          _startWidth: bg._startWidth,
          _startHeight: bg._startHeight,
          isRepeat: bg.isRepeat
        }
      })
    },
    matting (e) {
      if (!this.selected || this.selected._type !== 'image') return
      this.selected.sprite.texture = PIXI.Texture.from(e.src)
      this.selected.entity.name = e.name
      this.selected.entity.source = e.name
      this.$store.commit('updateCropItem', {
        item: this.selected.entity
      })
    },
    async changeImgColor (color) {
      if (!this.selected || this.selected._type !== 'image') return
      if (color == null || color === '') {
        const sourceBase64 = await getItem(this.selected.entity.source)
        if (!this.selected || this.selected._type !== 'image') return
        this.selected.sprite.texture = PIXI.Texture.from(sourceBase64)
        this.selected.entity.color = '#ffffff'
        const newName = moment().format('YYYYMMDDHHmmss') + '.png'
        this.selected.entity.name = newName
        this.$store.commit('updateCropItem', {
          item: this.selected.entity
        })
      } else {
        if (this.selected.sprite._orignUrl == null) {
          this.selected.sprite._orignUrl = await getItem(this.selected.entity.source)
        }
        if (color.indexOf('rgb(') > -1) {
          const arr = color.replace('rgb(', '').replace(')', '').split(',')
          color = rgbToHex(Number.parseInt(arr[0]), Number.parseInt(arr[1]), Number.parseInt(arr[2]))
        }
        changeImageColor(this.selected.sprite._orignUrl, color, (newBase64) => {
          this.selected.sprite.texture = PIXI.Texture.from(newBase64)
          this.selected.entity.color = color
          const newName = moment().format('YYYYMMDDHHmmss') + '.png'
          this.selected.entity.name = newName
          this.$store.commit('updateCropItem', {
            item: this.selected.entity
          })
          if (!this.$store.state.ui.os.isPC) {
            setItem(newName, newBase64)
          } else {
            this.temImageData = {
              name: newName,
              src: newBase64
            }
          }
        })
      }
    },
    async confirmColor () {
      // 保存改变颜色后的图片,用于还原设计
      await setItem(this.temImageData.name, this.temImageData.src)
      this.temImageData = {
        name: '',
        src: ''
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.editor-content {
  position: absolute;
  top: 0;
}
</style>
